<template>
  <section class="question-options-preview">
    <v-btn
      v-if="adminEditMode"
      type="button"
      class="question-options-preview__edit mb-3"
      color="primary"
      density="comfortable"
      prepend-icon="mdi-pencil"
      size="small"
      variant="tonal"
      @click="emit('edit-section', 'answers')"
    >
      Edit answers
    </v-btn>

    <div
      class="answers-grid"
      :class="{ 'answers-grid--images': hasAnswerImages }"
    >
      <div
        v-for="(opt, i) in options"
        :key="opt?.oID || i"
        class="py-2 px-4 rounded-lg noselect answer-card"
        :class="{
          'mt-1': !hasAnswerImages,
          'answer-div': !showResults,
          'elevation-3': showResults && opt?.oID === chosenAnswerId,
          'bg-slightBlue': opt?.oID !== chosenAnswerId || !showResults,
          'bg-green-lighten-4':
            opt?.oID === chosenAnswerId && opt?.isCorrect && showResults,
          'bg-red-lighten-4':
            opt?.oID === chosenAnswerId && !opt?.isCorrect && showResults,
        }"
        v-motion
        :initial="{ opacity: 0, y: 10 }"
        :enter="{ opacity: 1, y: 0, scale: 1 }"
        :delay="100 * (i + 1)"
        @click="!disabled && emit('answer', { option: opt, index: i })"
        :aria-disabled="disabled"
      >
        <v-row>
          <v-col :id="'a' + i" md="1" class="align-self-center">
            <v-icon
              v-if="opt?.oID === chosenAnswerId && !showResults"
              icon="mdi-circle-slice-8"
              color=""
            />
            <v-icon
              v-if="opt?.oID !== chosenAnswerId && !showResults"
              icon="mdi-circle-outline"
              color=""
            />
            <v-icon
              v-if="showResults && opt?.isCorrect"
              icon="mdi-check-circle"
              color="green"
            />
            <v-icon
              v-if="showResults && !opt?.isCorrect"
              icon="mdi-close-circle"
              color="red"
            />
          </v-col>
          <v-col cols="12" md="11">
            <div
              v-if="hasOptionText(opt)"
              class="v-html-content"
              v-html="getOptionTextHtml(opt)"
            ></div>
            <div
              v-else-if="getOptionImageId(opt)"
              class="text-caption text-medium-emphasis"
            >
              Image answer option
            </div>
            <div v-else class="text-caption text-error">
              Option content unavailable
            </div>
          </v-col>
          <v-col
            v-if="showResults && opt?.answerExplanation"
            class="font-italic v-html-content"
            cols="12"
            v-html="formatAnswerExplanation(opt.answerExplanation)"
          ></v-col>
          <slot name="option-stats" :option="opt" :index="i"></slot>
        </v-row>
        <div
          v-if="getOptionImageId(opt)"
          class="answer-image-wrapper mt-2"
          @click.stop
        >
          <ImgExpander height="160" :src="allImgPaths[getOptionImageId(opt)]" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";

import ImgExpander from "@/components/ImgExpander.vue";

const props = defineProps({
  disabled: { type: Boolean, default: false },
  options: {
    type: Array,
    default: () => [],
  },
  showResults: {
    type: Boolean,
    default: false,
  },
  chosenAnswerId: {
    type: [String, Number],
    default: null,
  },
  allImgPaths: {
    type: Object,
    required: true,
  },
  adminEditMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["answer", "edit-section"]);

const normalizeAnswerImageId = (value) => {
  if (Array.isArray(value)) return value[0] || "";
  if (typeof value === "string") return value;
  return "";
};

const getOptionImageId = (option) =>
  normalizeAnswerImageId(option?.answerImageId ?? option?.answerImageIds ?? "");

const hasOptionText = (option) =>
  typeof option?.text === "string" && option.text.trim().length > 0;

const getOptionTextHtml = (option) => option?.text || "";

const hasAnswerImages = computed(() =>
  props.options.some((opt) => Boolean(getOptionImageId(opt))),
);

const formatAnswerExplanation = (value) => {
  if (typeof value !== "string" || value.length === 0) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};
</script>

<style lang="scss" scoped>
.answer-div {
  border: 1px solid #fff;
}

.answer-div:hover {
  cursor: pointer;
  border: 1px solid #333;
}

.answers-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answers-grid--images {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.answer-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
}

.answer-image-wrapper {
  border-radius: 8px;
  overflow: hidden;
}

@media (max-width: 960px) {
  .answers-grid--images {
    grid-template-columns: 1fr;
  }
}
</style>
